import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import api from "../../util/api";

const initialState = {
  createRoute: {
    loading: false,
  },
  importRoute: {
    loading: false,
  },
  getRoute: {
    loading: false,
    data: [],
    pagination: {
      page: 1,
      pageSize: 10,
      totalItems: 0,
    },
  },
  deleteRoute: {
    loading: false,
  },
  editRoute: {
    loading: false,
  },
  searchAndGetRoute: {
    loading: false,
    data: [],
  },
  deleteAllRoute: {
    loading: false,
  },
  deleteSelectedRoute: {
    loading: false,
  },
};

//Route API call
const asyncCreateRoute = createAsyncThunk(
  "admin/createRoute",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post("/api/routes/create", payload);
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message,
      });
    }
  }
);

const asyncImportRoute = createAsyncThunk(
  "admin/importRoute",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post("/api/routes/import", payload, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message,
      });
    }
  }
);

const asyncGetRoute = createAsyncThunk(
  "admin/getRoute",
  async (payload, { rejectWithValue }) => {
    const {
      page = 1,
      limit = 10,
      sortByColumn = "",
      sortOrder = "",
      q = "",
    } = payload;
    try {
      const response = await api.get(
        `/api/routes?page=${page}&limit=${limit}&sortByColumn=${sortByColumn}&sortOrder=${sortOrder}&q=${q}`
      );
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message,
      });
    }
  }
);

const asyncDeleteRoute = createAsyncThunk(
  "admin/deleteRoute",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/api/routes/${payload}`);
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message,
      });
    }
  }
);

const asyncEditRoute = createAsyncThunk(
  "admin/editRoute",
  async (payload, { rejectWithValue }) => {
    const { id, ...restPayload } = payload;
    try {
      const response = await api.put(`/api/routes/${id}`, restPayload);
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message,
      });
    }
  }
);

const asyncSearchAndGetRoute = createAsyncThunk(
  "admin/searchAndGetRoute",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/routes/search`);
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message,
      });
    }
  }
);

const asyncDeleteAllRoute = createAsyncThunk(
  "admin/deleteAllRoute",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/api/routes/delete-all`);
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message,
      });
    }
  }
);

const asyncDeleteSelectedRoute = createAsyncThunk(
  "admin/deleteSelectedRoute",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post(`/api/routes/delete-many`, payload);
      return response;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message,
      });
    }
  }
);

export const routeSlice = createSlice({
  name: "route",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // create route
    builder.addCase(asyncCreateRoute.pending, (state) => {
      state.createRoute = {
        loading: true,
      };
    });
    builder.addCase(asyncCreateRoute.fulfilled, (state) => {
      state.createRoute = {
        loading: false,
      };
    });
    builder.addCase(asyncCreateRoute.rejected, (state) => {
      state.createRoute = {
        loading: false,
      };
    });
    // import route
    builder.addCase(asyncImportRoute.pending, (state) => {
      state.importRoute = {
        loading: true,
      };
    });
    builder.addCase(asyncImportRoute.fulfilled, (state) => {
      state.importRoute = {
        loading: false,
      };
    });
    builder.addCase(asyncImportRoute.rejected, (state) => {
      state.importRoute = {
        loading: false,
      };
    });
    // get route
    builder.addCase(asyncGetRoute.pending, (state) => {
      state.getRoute = {
        loading: true,
        data: state.getRoute.data || [],
        pagination: state.getRoute.pagination,
      };
    });
    builder.addCase(asyncGetRoute.fulfilled, (state, action) => {
      state.getRoute = {
        loading: false,
        data: action.payload.data.results || [],
        pagination: action.payload.data.pagination || state.getRoute.pagination,
      };
    });
    builder.addCase(asyncGetRoute.rejected, (state) => {
      state.getRoute = {
        loading: false,
        data: [],
        pagination: {
          page: 1,
          pageSize: 10,
          totalItems: 0,
        },
      };
    });
    // delete route
    builder.addCase(asyncDeleteRoute.pending, (state) => {
      state.deleteRoute = {
        loading: true,
      };
    });
    builder.addCase(asyncDeleteRoute.fulfilled, (state) => {
      state.deleteRoute = {
        loading: false,
      };
    });
    builder.addCase(asyncDeleteRoute.rejected, (state) => {
      state.deleteRoute = {
        loading: false,
      };
    });
    // edit route
    builder.addCase(asyncEditRoute.pending, (state) => {
      state.editRoute = {
        loading: true,
      };
    });
    builder.addCase(asyncEditRoute.fulfilled, (state) => {
      state.editRoute = {
        loading: false,
      };
    });
    builder.addCase(asyncEditRoute.rejected, (state) => {
      state.editRoute = {
        loading: false,
      };
    });
    // search and get route
    builder.addCase(asyncSearchAndGetRoute.pending, (state) => {
      state.searchAndGetRoute = {
        loading: true,
        data: state.searchAndGetRoute.data || [],
      };
    });
    builder.addCase(asyncSearchAndGetRoute.fulfilled, (state, action) => {
      state.searchAndGetRoute = {
        loading: false,
        data: action.payload.data.results || state.searchAndGetRoute.data,
      };
    });
    builder.addCase(asyncSearchAndGetRoute.rejected, (state) => {
      state.searchAndGetRoute = {
        loading: false,
        data: [],
      };
    });
    // delete all route
    builder.addCase(asyncDeleteAllRoute.pending, (state) => {
      state.deleteAllRoute = {
        loading: true,
      };
    });
    builder.addCase(asyncDeleteAllRoute.fulfilled, (state) => {
      state.deleteAllRoute = {
        loading: false,
      };
    });
    builder.addCase(asyncDeleteAllRoute.rejected, (state) => {
      state.deleteAllRoute = {
        loading: false,
      };
    });
    // delete selected route
    builder.addCase(asyncDeleteSelectedRoute.pending, (state) => {
      state.deleteSelectedRoute = {
        loading: true,
      };
    });
    builder.addCase(asyncDeleteSelectedRoute.fulfilled, (state) => {
      state.deleteSelectedRoute = {
        loading: false,
      };
    });
    builder.addCase(asyncDeleteSelectedRoute.rejected, (state) => {
      state.deleteSelectedRoute = {
        loading: false,
      };
    });
  },
});

export {
  asyncCreateRoute,
  asyncImportRoute,
  asyncGetRoute,
  asyncDeleteRoute,
  asyncEditRoute,
  asyncSearchAndGetRoute,
  asyncDeleteAllRoute,
  asyncDeleteSelectedRoute,
};

export default routeSlice.reducer;
